import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  LineChart, Legend,
} from "recharts";
import type { BucketSeries, ChurnRiskLevel, ChurnStatus } from "./helpers";
import { fmtN, fmtPct, fmtPctDelta } from "./helpers";

export const PRIMARY = "#2F7A8A";
export const CORAL   = "#DC6A4C";
export const VIOLET  = "#8352C0";
export const JADE    = "#1E6D5B";
export const MUTED   = "#A5A196";
export const FG1     = "#1F1E1A";
export const FG3     = "#7A766C";

const CONV_BAR_FILL = "rgba(131, 82, 192, 0.18)";

export function KpiCard({
  label, value, sub, trendPct, icon,
}: {
  label: string;
  value: string;
  sub?: string;
  trendPct?: number | null;
  icon?: string;
}) {
  const trend = typeof trendPct === "number" && Number.isFinite(trendPct) ? trendPct : null;
  const isDown = trend !== null && trend < 0;
  const isUp   = trend !== null && trend > 0;
  return (
    <div className="kpi">
      <div className="kpi-label">
        <span>{label}</span>
        {icon && <i className={`ti ${icon}`} style={{ fontSize: 14, color: VIOLET, marginLeft: "auto" }} />}
      </div>
      <div className="kpi-value" style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span>{value}</span>
        {trend !== null && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 7px",
              borderRadius: 999,
              background: isDown ? "var(--color-coral-soft)" : isUp ? "var(--color-jade-soft)" : "var(--bg-3)",
              color: isDown ? "var(--color-coral-hover)" : isUp ? "var(--color-jade)" : "var(--fg-3)",
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
            }}
          >
            <i className={`ti ti-${isDown ? "trending-down" : isUp ? "trending-up" : "minus"}`} style={{ fontSize: 11 }} />
            {fmtPctDelta(trend)}
          </span>
        )}
      </div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

export function MeetingsFunnelChart({ data }: { data: BucketSeries[] }) {
  if (data.length === 0) return <div className="empty">No data in range.</div>;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <ComposedChart data={data} margin={{ top: 8, right: 40, left: 0, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} interval="preserveStartEnd" />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: 10, fill: MUTED }}
          width={44}
          tickFormatter={(v) => (Number(v) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : String(v))}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={{ fontSize: 10, fill: MUTED }}
          width={36}
          domain={[0, 100]}
          tickFormatter={(v) => `${v}%`}
        />
        <Tooltip
          cursor={{ fill: "rgba(0,0,0,0.03)" }}
          formatter={(value, name) => {
            const n = Number(value);
            if (name === "conversionPct") return [`${n.toFixed(1)}%`, "Transcript to summary"];
            if (name === "meetings") return [fmtN(n), "Meetings"];
            if (name === "transcripts") return [fmtN(n), "Meetings w/ transcript"];
            if (name === "summaries") return [fmtN(n), "Meetings w/ transcript + summary"];
            return [fmtN(n), String(name)];
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
          iconType="plainline"
          formatter={(value) => {
            if (value === "meetings") return "Meetings";
            if (value === "transcripts") return "Meetings w/ transcript";
            if (value === "summaries") return "Meetings w/ transcript + summary";
            if (value === "conversionPct") return "Transcript to summary conversion";
            return value;
          }}
        />
        <Bar yAxisId="right" dataKey="conversionPct" fill={CONV_BAR_FILL} isAnimationActive={false} barSize={18} />
        <Line yAxisId="left" type="monotone" dataKey="meetings"    stroke={CORAL}   strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line yAxisId="left" type="monotone" dataKey="transcripts" stroke={VIOLET}  strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line yAxisId="left" type="monotone" dataKey="summaries"   stroke={PRIMARY} strokeWidth={2} dot={false} isAnimationActive={false} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

export function EngagementChart({ data }: { data: BucketSeries[] }) {
  if (data.length === 0) return <div className="empty">No data in range.</div>;
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: MUTED }} interval="preserveStartEnd" />
        <YAxis
          tick={{ fontSize: 10, fill: MUTED }}
          width={44}
          tickFormatter={(v) => (Number(v) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : String(v))}
        />
        <Tooltip
          cursor={{ fill: "rgba(0,0,0,0.03)" }}
          formatter={(value, name) => {
            const n = Number(value);
            if (name === "meetingChats") return [fmtN(n), "Ask Marloo (Meeting)"];
            if (name === "clientChats")  return [fmtN(n), "Ask Marloo (Client)"];
            return [fmtN(n), String(name)];
          }}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
          iconType="plainline"
          formatter={(value) => {
            if (value === "meetingChats") return "Ask Marloo (Meeting)";
            if (value === "clientChats")  return "Ask Marloo (Client)";
            return value;
          }}
        />
        <Line type="monotone" dataKey="meetingChats" stroke={JADE}   strokeWidth={2} dot={false} isAnimationActive={false} />
        <Line type="monotone" dataKey="clientChats"  stroke={VIOLET} strokeWidth={2} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export function StatusBadge({ status }: { status: ChurnStatus }) {
  return (
    <span className={`badge ${status === "Active" ? "b-red" : "b-violet"}`}>
      {status}
    </span>
  );
}

export function RiskBadge({ risk }: { risk: ChurnRiskLevel }) {
  const cls = risk === "High" ? "b-orange" : risk === "Medium" ? "b-violet" : "b-green";
  return <span className={`badge ${cls}`}>{risk}</span>;
}

export function TierBadge({ tier }: { tier: string }) {
  const t = tier.toLowerCase();
  const cls = t === "pro" ? "b-blue" : t === "plus" ? "b-violet" : "b-gray";
  return <span className={`badge ${cls}`}>{tier}</span>;
}

export function FunnelTable({ data }: { data: BucketSeries[] }) {
  if (data.length === 0) return null;
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>Bucket</th>
            <th className="num">Meetings</th>
            <th className="num">Meetings w/ transcript</th>
            <th className="num">Meetings w/ transcript + summary</th>
            <th className="num">Transcript to summary</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td className="num">{fmtN(row.meetings)}</td>
              <td className="num">{fmtN(row.transcripts)}</td>
              <td className="num">{fmtN(row.summaries)}</td>
              <td className="num">{fmtPct(row.summaries, row.transcripts)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function EngagementTable({ data }: { data: BucketSeries[] }) {
  if (data.length === 0) return null;
  return (
    <div className="table-wrap">
      <table className="t">
        <thead>
          <tr>
            <th>Bucket</th>
            <th className="num">Ask Marloo (Meeting)</th>
            <th className="num">Ask Marloo (Client)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td className="num">{fmtN(row.meetingChats)}</td>
              <td className="num">{fmtN(row.clientChats)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
